// The preview of a distribution job on the Activities page: the rows as a table, and for "Where visitors come from"
// a Top origins panel. A preview stops at 100 rows, so when the API says it was cut the table says so and the panel
// reads every row from the job's full export: arrivals summed from part of the rows would read as totals.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '../../components/common/NavBar';
import CsvDownloadButton from '../../components/common/CsvDownloadButton';
import AboutThisData from '../../components/data/AboutThisData';
import DataStateNotice from '../../components/data/DataStateNotice';
import ExportAllRowsButton, { EXPORT_ALL_ROWS_LABEL } from '../../components/data/ExportAllRowsButton';
import SourceBadge from '../../components/data/SourceBadge';
import { COUNTRIES, normalizeCountry } from '../../config/countries';
import { ALL_ROLES_LABEL, DISTRIBUTIONS, DISTRIBUTION_ROLE_LABELS, GRANULARITY_LABELS, pendingFor } from '../../config/products';
import type { DistributionRole } from '../../config/products';
import { fullDistributionRows } from '../../services/activityDistributionsApi';
import type { ActivitiesRequest, DistributionJobParams, DistributionRow } from '../../services/activityDistributionsApi';
import { describeRequestError, isAbortError } from '../../services/http';
import { DISTRIBUTION_CSV_HEADERS, bucketsOf, busiestBucket, distributionCsvCells, sortByArrivals, topOrigins } from '../../utils/activityDistributions';
import { csvFilename } from '../../utils/csv';
import type { Cell } from '../../utils/csv';
import { formatDayLabel } from '../../utils/dateWindow';
import { EE_ORIGIN_TOP, EE_OTHER_AREAS, foldOrigins, shareText } from '../../utils/eeActivities';
import { CONTEXT_HEADERS, contextValues } from '../../utils/exportContext';
import { activityBackState } from '../../utils/returnState';
import type { ActivityHandoff } from '../../utils/returnState';

export const CUT_PREVIEW_TEXT = `This preview stops at 100 rows and there are more. Use ${EXPORT_ALL_ROWS_LABEL} for every row.`;

/** A figure as a cell: blank when withheld, as "About this data" says. */
const figure = (v: number | null) => (v === null ? '' : v.toLocaleString());
const roleLabel = (role: string | null) => (role && role in DISTRIBUTION_ROLE_LABELS ? DISTRIBUTION_ROLE_LABELS[role as DistributionRole] : role ?? '');

const OriginsSection: React.FC<{ picker?: React.ReactNode; children: React.ReactNode }> = ({ picker, children }) => (
  <section aria-label="Top origins" className="bg-surface rounded-lg shadow p-6 mb-6">
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <div>
        <h2 className="text-lg font-semibold text-ink">Top origins</h2>
        <p className="text-sm text-ink-muted">Arrivals summed across the selected areas. People are not summed: one person can be counted in several.</p>
      </div>
      {picker}
    </div>
    {children}
  </section>
);

/** Where arrivals come from, summed over `rows`, which must be every row. Hours are not added up, so an hourly job shows one at a time. */
const OriginFigures: React.FC<{ rows: DistributionRow[] }> = ({ rows }) => {
  const buckets = useMemo(() => bucketsOf(rows), [rows]);
  const [bucket, setBucket] = useState(() => busiestBucket(rows));
  const origins = useMemo(() => foldOrigins(topOrigins(rows.filter((r) => r.time_bucket === bucket)), EE_ORIGIN_TOP, EE_OTHER_AREAS), [rows, bucket]);
  const picker = buckets.length > 1 ? (
    <label className="flex items-center gap-2 text-sm text-ink">
      Time bucket (UTC)
      <select className="select select-sm select-bordered" value={bucket ?? ''} onChange={(e) => setBucket(e.target.value)}>
        {buckets.map((b) => (
          <option key={b} value={b}>
            {b}
          </option>
        ))}
      </select>
    </label>
  ) : null;
  return (
    <OriginsSection picker={picker}>
      {origins.length === 0 ? (
        <p className="text-sm text-ink-muted">No published arrivals.</p>
      ) : (
        <table className="table table-sm w-full">
          <thead>
            <tr>
              <th>Origin</th>
              <th className="text-right">Arrivals</th>
              <th className="text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {origins.map((o) => (
              <tr key={o.band}>
                <td>{o.name}</td>
                <td className="text-right">{figure(o.arrivals)}</td>
                <td className="text-right">{shareText(o.share)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </OriginsSection>
  );
};

/** Top origins of a job: its preview rows when they are all of them, otherwise every row from its full export. */
const TopOrigins: React.FC<{ rows: DistributionRow[]; truncated: boolean; params: DistributionJobParams }> = ({ rows, truncated, params }) => {
  const [fullRows, setFullRows] = useState<DistributionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!truncated) return undefined;
    const controller = new AbortController();
    fullDistributionRows(params, { signal: controller.signal })
      .then(setFullRows)
      .catch((e) => {
        if (!isAbortError(e)) setError(describeRequestError(e));
      });
    return () => controller.abort();
  }, [truncated, params]);

  if (!truncated) return <OriginFigures rows={rows} />;
  if (fullRows) return <OriginFigures rows={fullRows} />;
  return (
    <OriginsSection>
      {error ? (
        <DataStateNotice tone="warn" title="Top origins need every row" body={`Sums from a cut preview would be wrong, so none are shown. ${error}`} />
      ) : (
        <p className="text-sm text-ink-muted">
          <span className="loading loading-spinner loading-xs mr-2"></span>
          Adding up every row…
        </p>
      )}
    </OriginsSection>
  );
};

export interface DistributionPreviewState extends Omit<ActivityHandoff, 'jobResults' | 'request'> {
  jobResults: DistributionRow[];
  request: Extract<ActivitiesRequest, { kind: 'distributions' }>;
}

const DistributionPreview: React.FC<{ state: DistributionPreviewState }> = ({ state }) => {
  const navigate = useNavigate();
  const { jobId, jobResults, request, day, granularity } = state;
  const truncated = state.truncated === true;
  const { distribution, role, activity_type: activityType } = request.params;
  const country = normalizeCountry(state.countryCode);
  const origins = DISTRIBUTIONS[distribution].bands === 'origin';
  const distributionLabel = DISTRIBUTIONS[distribution].label;
  const pickedRole = role ? DISTRIBUTION_ROLE_LABELS[role] : ALL_ROLES_LABEL;
  const hourly = activityType === 'hourly';
  const rows = useMemo(() => sortByArrivals(jobResults), [jobResults]);
  const backState = activityBackState(state);

  const context: string[] = country ? contextValues(country, 'activities', 'arrivals', state.windowUtc ?? null) : CONTEXT_HEADERS.map(() => '');
  context[CONTEXT_HEADERS.indexOf('Measure')] = distributionLabel;
  const csvRows: Cell[][] = [[...DISTRIBUTION_CSV_HEADERS, ...CONTEXT_HEADERS], ...rows.map((r): Cell[] => [...distributionCsvCells(r), ...context])];
  const label = [country ? COUNTRIES[country].label : '', day, granularity, distribution, role].filter(Boolean).join(' ');

  return (
    <div className="min-h-screen bg-base-200">
      <NavBar title="Activity Results Preview" showBackButton backTo="/activities-map" backState={backState} />

      <div className="container mx-auto px-4 py-6">
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold text-ink">{distributionLabel}</h1>
            {country ? <SourceBadge country={country} product="activities" /> : null}
          </div>
          <p className="text-ink-muted mt-1">Job ID: {jobId}</p>
          <p className="text-ink-muted mt-1">
            {pickedRole}
            {day ? ` · ${formatDayLabel(day)}` : ''}
            {granularity ? ` · ${GRANULARITY_LABELS[granularity]}` : ''} · UTC
          </p>
          {country ? (
            <div className="mt-4">
              <AboutThisData country={country} product="activities" distributions />
            </div>
          ) : null}
          {jobResults.length === 0 ? (
            <div className="mt-4">
              <DataStateNotice
                tone="info"
                title="No rows for this selection"
                body={['Cells or municipalities with fewer than 5 people are not published.', pendingFor(country, 'activities', state.geoType)?.reason].filter(Boolean).join(' ')}
              />
            </div>
          ) : null}
          {truncated ? (
            <div className="mt-4">
              <DataStateNotice tone="warn" title="Only part of the rows" body={CUT_PREVIEW_TEXT} />
            </div>
          ) : null}
        </div>

        {origins && jobResults.length > 0 ? <TopOrigins rows={jobResults} truncated={truncated} params={request.params} /> : null}

        {jobResults.length > 0 ? (
          <div className="bg-surface rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Role</th>
                    <th>{origins ? 'Origin' : 'Band'}</th>
                    {hourly ? <th>Time bucket (UTC)</th> : null}
                    <th className="text-right">People</th>
                    <th className="text-right">Arrivals</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.geo_id}-${r.band}-${i}`}>
                      <td>{r.geo_name ?? r.geo_id}</td>
                      <td>{roleLabel(r.role)}</td>
                      <td>{r.band_name ?? r.band}</td>
                      {hourly ? <td className="text-sm">{r.time_bucket}</td> : null}
                      <td className="text-right">{figure(r.people)}</td>
                      <td className="text-right">{figure(r.arrivals)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap justify-center items-start mt-6 gap-4">
          <button className="btn btn-outline" onClick={() => navigate('/activities-map', { state: backState })}>
            ← Back to Results
          </button>
          <CsvDownloadButton rows={csvRows} filename={csvFilename('activities-preview', label)} disabled={jobResults.length === 0} label="Export preview (CSV)" />
          <ExportAllRowsButton request={request} filename={csvFilename('activities-all-rows', label)} />
        </div>
      </div>
    </div>
  );
};

export default DistributionPreview;
